import React from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';

import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import Message from 'components/Message';

import { messages } from '../ServicePricingSection.messages';
import { isImageMime, truncateFilename, PROOF_ITEM_STATUS } from './proofFileUpload.helpers';

const { block, element } = bem('proof-file-upload-item');

const statusIconFor = status => {
  switch (status) {
    case PROOF_ITEM_STATUS.PENDING:
      return ICONS.clock;
    case PROOF_ITEM_STATUS.UPLOADING:
      return ICONS.spinner;
    case PROOF_ITEM_STATUS.SUCCESS:
      return ICONS.checkmarkInCircleThickGreen;
    case PROOF_ITEM_STATUS.ERROR:
      return ICONS.error;
    default:
      return null;
  }
};

const ProofFileUploadItem = ({ item, onRemove, onRetry }) => {
  const intl = useIntl();
  const { id, originalFileName, mimeType, status, downloadUrl } = item;
  const fileTypeIcon = isImageMime(mimeType) ? ICONS.paper : ICONS.fileImport;
  const statusIcon = statusIconFor(status);

  return (
    <div className={block({ status, error: status === PROOF_ITEM_STATUS.ERROR })}>
      <Isvg src={fileTypeIcon} className={element('type-icon')} />

      <span className={element('filename')} title={originalFileName}>
        {truncateFilename(originalFileName)}
      </span>

      {statusIcon && (
        <Isvg
          src={statusIcon}
          className={element('status-icon', { spinning: status === PROOF_ITEM_STATUS.UPLOADING })}
        />
      )}

      {status === PROOF_ITEM_STATUS.ERROR && onRetry && (
        <button
          type="button"
          onClick={() => onRetry(id)}
          className={element('retry')}
          aria-label={intl.formatMessage(messages.proofRetryFile)}>
          <Message message={messages.proofRetryFile} />
        </button>
      )}

      {downloadUrl && (
        <a
          href={downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={element('download')}
          aria-label={intl.formatMessage(messages.proofDownloadFile)}>
          <Isvg src={ICONS.openExternal} />
        </a>
      )}

      <button
        type="button"
        onClick={() => onRemove(id)}
        className={element('remove')}
        aria-label={intl.formatMessage(messages.proofRemoveFile)}>
        <Isvg src={ICONS.crossSmall} />
      </button>

      {status === PROOF_ITEM_STATUS.ERROR && item.errorMessage && (
        <Message as="span" message={item.errorMessage} className={element('error-text')} />
      )}
    </div>
  );
};

ProofFileUploadItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    originalFileName: PropTypes.string.isRequired,
    mimeType: PropTypes.string,
    status: PropTypes.oneOf(Object.values(PROOF_ITEM_STATUS)).isRequired,
    downloadUrl: PropTypes.string,
    errorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }).isRequired,
  onRemove: PropTypes.func.isRequired,
  onRetry: PropTypes.func,
};

export default ProofFileUploadItem;
